import React from 'react'
import {MarkerClusterer, SuperClusterAlgorithm, defaultOnClusterClickHandler} from '@googlemaps/markerclusterer'

import {makeRequest} from './http'
import Button from './components/button'
import get from './components/get'
import GoogleMapWrapper from './components/google-map-wrapper'
import AddIcon from './components/icons/add-icon'
import LocateIcon from './components/icons/locate-icon'
import {useInit} from './components/init-context'
import LoadMore from './components/load-more'
import {iconPaths} from './components/main-rating'
import Navigation from './components/navigation'
import {objectStoreController} from './components/object-store'
import {useGet} from './components/object-store'
import {useList} from './components/object-store'
import {useMerge} from './components/object-store'
import {useObject} from './components/object-store'
import {useSaveListener} from './components/object-store'
import {useSave} from './components/object-store'
import Paging from './components/paging'
import PopupMenu from './components/popup-menu'
import ProducerEditorModal from './components/producer-editor-modal'
import ProducerList from './components/producer-list'
import {ProducerContent} from './components/producer-view'
import {ProducerFooter} from './components/producer-view'
import {ProducerHeader} from './components/producer-view'
import {useProducerProps} from './components/producer-view'
import {ProductSheet} from './components/product-view'
import Query from './components/query'
import RatingQuery from './components/rating-query'
import Sheet from './components/sheet'
import SortIcon from './components/sort-icon'
import {getColorForTag} from './components/tag'
import useDropResource from './hooks/use-drop-resource'
import useUpdatingRef from './hooks/use-updating-ref'
import useUserCoordinates from './hooks/use-user-coordinates'
import bm from './utils/bm'
import genId from './utils/gen_id'
import translateQuery from './utils/translate-query'
import useHash from './utils/use-hash'
import './app.scss'
import './page-map.scss'
import './sheets.scss'


const interestedInSaved = {
    models: {producer: true},
}

const homeCoordinates = {
    coordinate_latitudine: 51.10122666778197,
    coordinate_longitudine: 17.045190242932623,
}

// AdvancedMarkerElement requires a vector map id. 'DEMO_MAP_ID' works for development;
// replace with a real Cloud Map ID for production.
const MAP_ID = 'DEMO_MAP_ID'

// E: resolve the CSS --primary once (getColorForTag falls back to it) and cache per-tag colors,
// instead of calling getComputedStyle() inside every marker.
const PRIMARY_COLOR = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim()
const tagColorCache = new Map()
function colorForTagCached(tag) {
    if (!tagColorCache.has(tag)) {
        tagColorCache.set(tag, getColorForTag(tag))
    }
    return tagColorCache.get(tag)
}

function pinColor(tags) {
    const color = tags
        .filter(t => t !== '#' && t !== 'TODO')
        .map(colorForTagCached)
        .filter(c => c.trim() !== PRIMARY_COLOR)[0]
    return color || 'black'
}

function pinPath(rating, tags) {
    if (tags.indexOf('#') !== -1) {
        return iconPaths.hash
    }
    const todo = tags.indexOf('TODO') !== -1
    if (todo && rating != null) {
        return iconPaths['todoDone' + rating]
    }
    if (todo) {
        return iconPaths.todo
    }
    return iconPaths['orating' + rating] || iconPaths.oratingNull
}

function pinOpacity(tags) {
    return tags.indexOf('closed') !== -1 ? 0.5 : 1
}

// E: memoize the marker SVG markup by (tags, rating, active)
const pinSvgCache = new Map()
function pinSvg(pin, active) {
    const key = pin.tags.join(',') + '|' + pin.rating + '|' + (active ? 1 : 0)
    let svg = pinSvgCache.get(key)
    if (!svg) {
        const size = active ? 34 : 22
        svg = `<svg width="${size}" height="${size}" viewBox="0 0 100 100" style="display:block;opacity:${pinOpacity(pin.tags)}">`
            + `<path d="${pinPath(pin.rating, pin.tags)}" fill="${pinColor(pin.tags)}" stroke="white" stroke-width="${active ? 6 : 4}"/></svg>`
        pinSvgCache.set(key, svg)
    }
    return svg
}

function pinElement(pin, active) {
    const div = document.createElement('div')
    div.style.transform = 'translateY(50%)' // center the icon over the coordinate
    div.innerHTML = pinSvg(pin, active)
    return div
}

function boundsContain(outer, inner) {
    return outer.south <= inner.south && outer.north >= inner.north
        && outer.west <= inner.west && outer.east >= inner.east
}

// C & D: load only the producers in the viewport, extended 2x so panning is less jarring.
function usePins(map, filter) {
    const [pins, setPins] = React.useState([])
    const loadedBoundsRef = React.useRef(null)
    const filterRef = useUpdatingRef(filter)
    const timeoutRef = React.useRef()

    const fetchPins = React.useCallback(() => {
        if (!map) {return}
        const b = map.getBounds()
        if (!b) {return}
        const ne = b.getNorthEast()
        const sw = b.getSouthWest()
        const visible = {south: sw.lat(), north: ne.lat(), west: sw.lng(), east: ne.lng()}
        if (loadedBoundsRef.current && boundsContain(loadedBoundsRef.current, visible)) {
            return // still inside the area we already loaded
        }
        const latSpan = visible.north - visible.south
        const lngSpan = visible.east - visible.west
        const bounds = {
            south: visible.south - latSpan / 2,
            north: visible.north + latSpan / 2,
            west: visible.west - lngSpan / 2,
            east: visible.east + lngSpan / 2,
        }
        loadedBoundsRef.current = bounds
        makeRequest('2/producer_pins', {body: JSON.stringify({bounds, filter: filterRef.current})})
            .then(response => setPins(response.pins))
            .catch(() => {})
    }, [map, filterRef])

    React.useEffect(() => {
        if (!map) {return}
        const onIdle = () => {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = setTimeout(fetchPins, 150)
        }
        const listener = map.addListener('idle', onIdle)
        return () => window.google.maps.event.removeListener(listener)
    }, [map, fetchPins])

    React.useEffect(() => {
        loadedBoundsRef.current = null // filter changed: invalidate and refetch
        fetchPins()
    }, [filter, fetchPins])

    return pins
}

// A + B: cluster the pins, drawing each as an AdvancedMarkerElement.
function usePinClusterer(map, pins, selectedProducerId, onSelectProducer, cancelMapClickRef) {
    const onSelectRef = useUpdatingRef(onSelectProducer)
    const markersByIdRef = React.useRef({})
    const selectedRef = React.useRef(null)

    // Build markers + clusterer only when the pin SET changes (not on selection),
    // so selecting a pin doesn't tear down and rebuild every marker.
    React.useEffect(() => {
        if (!map) {return}
        const {AdvancedMarkerElement} = window.google.maps.marker
        const byId = {}
        const markers = pins.map(pin => {
            const marker = new AdvancedMarkerElement({
                position: {lat: pin.lat, lng: pin.lng},
                content: pinElement(pin, pin.id === selectedRef.current),
                gmpClickable: true,
            })
            marker.pinData = pin
            marker.addListener('click', () => {
                // The map's mouseup (which sets the sorting location) fires just
                // before this marker click — cancel it so selecting a producer
                // doesn't move the sorting location.
                if (cancelMapClickRef && cancelMapClickRef.current) {
                    clearTimeout(cancelMapClickRef.current)
                    cancelMapClickRef.current = null
                }
                onSelectRef.current(pin.id, {
                    coordinate_latitudine: pin.lat,
                    coordinate_longitudine: pin.lng,
                })
            })
            byId[pin.id] = marker
            return marker
        })
        markersByIdRef.current = byId
        // Larger cluster radius => fewer individual DOM markers on screen => smoother panning.
        const clusterer = new MarkerClusterer({
            map,
            markers,
            algorithm: new SuperClusterAlgorithm({radius: 110, maxZoom: 15}),
            // Like the marker click: cancel the pending map-click so clicking a
            // cluster zooms in without moving the sorting location.
            onClusterClick: (event, cluster, clickedMap) => {
                if (cancelMapClickRef && cancelMapClickRef.current) {
                    clearTimeout(cancelMapClickRef.current)
                    cancelMapClickRef.current = null
                }
                defaultOnClusterClickHandler(event, cluster, clickedMap)
            },
        })
        return () => {
            clusterer.clearMarkers()
            markers.forEach(m => {m.map = null})
            markersByIdRef.current = {}
        }
    }, [map, pins, onSelectRef])

    // On selection change, restyle only the two affected markers in place.
    React.useEffect(() => {
        const byId = markersByIdRef.current
        const prevId = selectedRef.current
        if (prevId && prevId !== selectedProducerId && byId[prevId]) {
            byId[prevId].content = pinElement(byId[prevId].pinData, false)
            byId[prevId].zIndex = null
        }
        const next = byId[selectedProducerId]
        if (next) {
            next.content = pinElement(next.pinData, true)
            next.zIndex = 999999
        }
        selectedRef.current = selectedProducerId
    }, [selectedProducerId, pins])
}


export default function MapPage({}) {
    const {hash, setHash, params, setHashParam} = useHash()

    let selectedProducerId = hash.startsWith('#producer/') ? hash.split('/')[1].split('&')[0] : null
    if (selectedProducerId === 'null') {
        selectedProducerId = null
    }
    let selectedProductId = selectedProducerId ? getProductId(hash) : null

    const [sort, setSort] = React.useState(localStorage.getItem('mapki2.sort') || 'distance')

    React.useEffect(() => {
        localStorage.setItem('mapki2.sort', sort)
    }, [sort])

    const [trigger, setTrigger] = React.useState(0)

    const savedQuery = useObject(get(params, 'query'))

    const [query, setQuery] = React.useState({'producer.fts': '', 'product.fts': '', ratings: '', page: {start: 0, end: 100}})
    const {userCoordinates, refreshUserCoordinates} = useUserCoordinates({
        ...homeCoordinates,
    })
    const [panToCoordinates, setPanToCoordinates] = React.useState(null)

    const [merging, setMerging] = React.useState(false)
    const [adding, setAdding] = React.useState(false)

    React.useEffect(() => {
        if (!merging) {
            setSelectedProducers([])
        }
    }, [merging])

    let [referenceCoordinates, setReferenceCoordinates] = useReferenceCoordinates()


    React.useEffect(() => {
        // Preserve other hash params (notably the selected saved query / filter) when
        // syncing the reference pin to the URL, so placing a pin doesn't reset them.
        const queryParam = get(params, 'query')
        const querySuffix = queryParam ? '&query=' + queryParam : ''
        if (!referenceCoordinates) {
            window.location.hash = '#map' + querySuffix
            return
        }
        if (
            userCoordinates &&
            referenceCoordinates.coordinate_latitudine == userCoordinates.coordinate_latitudine &&
            referenceCoordinates.coordinate_longitudine == userCoordinates.coordinate_longitudine
        ) {
            return
        }
        window.location.hash = '#map?rc=' + referenceCoordinates.coordinate_latitudine + ',' + referenceCoordinates.coordinate_longitudine + querySuffix
    }, [referenceCoordinates])
    referenceCoordinates = referenceCoordinates || userCoordinates

    const tSort = React.useMemo(() => {
        if (sort === 'created') {return [{field: 'producer._created_at', dir: 'DESC'}]}
        if (sort === 'updated') {return [{field: 'producer._updated_at', dir: 'DESC'}]}
        if (sort === 'distance') {return [{field: 'producer.distance', from: referenceCoordinates, dir: 'ASC'}]}
        return null
    }, [sort, referenceCoordinates])

    const tQuery = React.useMemo(() => {
        return translateQuery({...query, filter: get(savedQuery, 'query', 'filter')})
    }, [query, savedQuery])

    const active = (sort === 'distance' && !referenceCoordinates) ? false : true

    const {ids, refresh, total, spinning} = useList('producer', {...tQuery, sort: tSort, active})

    const [open, setOpen] = React.useState(localStorage.getItem('mapki2__app__master__open'))
    const [listSheetPosition, setListSheetPosition] = React.useState(localStorage.getItem('mapki2.app.masterPostion'))
    const [productSheetPosition, setProductSheetPosition] = React.useState(selectedProductId && selectedProducerId ? 'open' : 'closed')
    const [producerSheetPosition, setProducerSheetPosition_] = React.useState(selectedProducerId ? 'open' : 'closed')


    function setProducerSheetPosition(...x) {
        setProducerSheetPosition_(...x)
    }

    function toggleOpen() {
        setOpen(prev => {
            localStorage.setItem(localStorage.getItem('mapki2__app__master__open'), !prev)
            return !prev
        })
    }

    function setSelectedProducerId(id) {
        setHash('#producer/' + id, {query: get(params, 'query')})
    }

    useGet('producer', selectedProducerId)
    const selectedProducer = useObject(selectedProducerId)
    useGet('product', selectedProductId)
    const selectedProduct = useObject(selectedProductId)

    const [selectedProducers, setSelectedProducers] = React.useState([])


    React.useEffect(() => {
        document.body.classList.add('no-bounce')
        return () => document.body.classList.remove('no-bounce')
    }, [])

    function toggleProducer(id) {
        setSelectedProducers(p => {
            if (p.indexOf(id) === -1) {
                return [...p, id]
            }
            return p.filter(pid => pid !== id)
        })
    }

    useSaveListener(interestedInSaved, refresh)

    const save = useSave()

    function addProducer(update) {
        const id = genId()
        save('producer', {...update, id})
            .then(() => {
                setHashParam('edit', `producer/${id}`)
            })
    }

    function handleShowProducts(event, products) {
        setProducerSheetPosition('open')
        setHash(`#producer/${products[0].producer_id}/products/${products.map(p => p.id).join(',')}`, {query: get(params, 'query')})
    }

    const queryParam = get(params, 'query')
    const mergeProps = useMerge('producer', refresh)

    return <div className="app">
        <div className="app__map">
            <GoogleMap
                referenceCoordinates={referenceCoordinates}
                filter={tQuery.filter}
                onSelectProducer={(id, producer) => {
                    if (window.innerWidth < 1040 && producer) {
                        setPanToCoordinates(producer)
                    }
                    setSelectedProducerId(id)
                    setTimeout(() => {
                        setProducerSheetPosition('middle')
                    }, 200)
                }}
                onDoubleClick={addProducer}
                selectedProducerId={selectedProducerId}
                onClick={setReferenceCoordinates}
                userCoordinates={userCoordinates}
                panToCoordinates={panToCoordinates}
                mapRestriction={getMapRestriction()}
            />
            <div className="map__controls">
                <Navigation />
                <button
                    onClick={() => {
                        const prevReferenceCoordinates = referenceCoordinates
                        setPanToCoordinates({...userCoordinates})
                        setReferenceCoordinates(null)
                        refreshUserCoordinates()
                    }}
                    className="button button--square"
                >
                    <LocateIcon color="--on-primary"/>
                </button>
            </div>
        </div>
        <ProductSheet
            product={selectedProduct}
            producer={selectedProducer}
            setHash={setHash}
            producerSheetPosition={producerSheetPosition}
            productSheetPosition={productSheetPosition}
            setProductSheetPosition={setProductSheetPosition}
            params={params}
        />
        <ProducerSheet
            producer={selectedProducer}
            producerSheetPosition={producerSheetPosition}
            setProducerSheetPosition={setProducerSheetPosition}
            setHash={setHash}
            productSheetPosition={productSheetPosition}
            setProductSheetPosition={setProductSheetPosition}
            panTo={setPanToCoordinates}
            params={params}
            limit={false}
            producerLink={`#producer/${get(selectedProducer, 'id')}${queryParam ? ('&query=' + queryParam) : ''}`}
        />
        <Sheet
            position={producerSheetPosition === 'open' ? 'closed' : listSheetPosition}
            onChangePosition={setListSheetPosition}
            extraClassName={bm('list-sheet', {producerSheetPosition, productSheetPosition})}
            movesTextInput
        >
            <Content
                userCoordinates={userCoordinates}
                referenceCoordinates={referenceCoordinates}
                producers={ids}
                setQuery={setQuery}
                sort={sort}
                setSort={setSort}
                query={query}
                setSelectedProducers={setSelectedProducers}
                selectedProducers={selectedProducers}
                onSelectProducer={merging ? toggleProducer : (
                    (id, event) => {
                        setSelectedProducerId(id)
                        setTimeout(() => {
                            setProducerSheetPosition('middle')
                            const producer = get(objectStoreController.state, id)
                            setPanToCoordinates(producer)
                        }, 0)
                    }
                )}
                panTo={setPanToCoordinates}
                total={total}
                page={query.page}
                spinning={spinning}
                mergeProps={mergeProps}
                setProductSheetPosition={setProductSheetPosition}
                setProducerSheetPosition={setProducerSheetPosition}
                onShowProducts={handleShowProducts}
            >
            </Content>
        </Sheet>
        {params.edit ?
            <ProducerEditorModal />
            : null
        }
    </div>

    function getMapRestriction() {
        return `${listSheetPosition}-${producerSheetPosition}-${productSheetPosition}`
    }
}

function ProducerSheet(props) {
    const {hash} = useHash()

    const productsIds = React.useMemo(() => {
        if (hash.indexOf('/products/') !== -1) {
            const raw = hash.split('/products/')[1].split('/')[0].split('&')[0]
            if (raw) {
                return raw.split(',')
            }
        }
        return null
    }, [hash])


    const ps = useProducerProps(props)

    const {dropProps} = useDropResource({
        model: 'producer',
        modelId: get(props.producer, 'id'),
        parentClassName: 'producer-sheet',
    })

    return <Sheet
        extraClassName={bm("producer-sheet", {producerSheetPosition: props.producerSheetPosition, productSheetPosition: props.productSheetPosition})}
        position={props.producerSheetPosition}
        onChangePosition={props.setProducerSheetPosition}
        header
        footer
        id="producer"
        {...dropProps}
    >
        <ProducerHeader {...ps} />
        <ProducerContent {...ps} productsIds={productsIds} />
        <ProducerFooter {...ps} />
    </Sheet>
}


function BottomBar({open, disabled, onCancel, message, onConfirm}) {
    return <div className={bm('app__master__bottom-bar', {open})}>
        <button className="button" onClick={onCancel}>Cancel</button>
        <button disabled={disabled} className="button" onClick={onConfirm}>{message}</button>
    </div>
}


function getLimit(coordinates, query) {
    const longestLine = [query.producer, query.product].reduce((acc , v) => {
        return Math.max(acc, v.split('\n').reduce((acc, l) => Math.max(acc, l.length), 0))
    }, 0)
    if (longestLine >= 3) {
        return 10000
    }
    return 200
}

function Content({
    userCoordinates,
    producers,
    onSelectProducer,
    onRequestOpen,
    setQuery,
    query,
    children,
    total,
    parent,
    page,
    spinning,
    setProducerSheetPosition,
    setProductSheetPosition,
    onShowProducts,
    ...props
}) {
    const selectable = get(props, 'mergeProps', 'mergeActive')
    const {setHash, params} = useHash()

    return <div className="content">
        <div className="query">
            <Query onFocus={onRequestOpen} name="Search producers, products..." value={query['fts']} onChange={producer => setQuery(prev => ({...prev, 'fts': producer}))} />
            {/*<Query onFocus={onRequestOpen} name="Producers..." value={query['producer.fts']} onChange={producer => setQuery(prev => ({...prev, 'producer.fts': producer}))} />*/}
            {/*<Query onFocus={onRequestOpen} name="Products..." value={query['product.fts']} onChange={product => setQuery(prev => ({...prev, 'product.fts': product}))} />*/}
            <RatingQuery
                value={query['product_rating.normalized_rating']} onChange={value => setQuery(prev => ({...prev, 'product_rating.normalized_rating': value}))}
            />
            <div className="query__buttons">
                <PopupMenu
                    onSelect={props.setSort}
                    value={props.sort}
                    closeOnSelect
                    values={[
                        {message: 'distance', value: 'distance'},
                        {message: 'created', value: 'created'},
                        {message: 'updated', value: 'updated'},
                    ]}
                >
                    <SortIcon width="16px" height="16px" color="white" /> {props.sort}
                </PopupMenu>
                <ProducerQuerySwitcher />
                {children}
                <span className="grower" />
                <Paging page={page} total={total} />
            </div>
        </div>
        <ProducerList
            producers={producers}
            userCoordinates={userCoordinates}
            {...props}
            selectable={selectable}
            selected={get(props, 'mergeProps', 'mainToMergeSelection')}
            onSelectProducer={get(props, 'mergeProps', 'mergeActive') ? get(props, 'mergeProps', 'addToMerge') : onSelectProducer}
            toggleMergeActive={get(props, 'mergeProps', 'addToMergeAlways')}
            onShowProduct={(event, p) => {
                setHash(`producer/${p.producer_id}/${p.id}`, {query: get(params, 'query')})
                setTimeout(() => {
                    setProductSheetPosition('open')
                    setProducerSheetPosition('middle')
                }, 0)
            }}
            onShowProducts={onShowProducts}
        />
        <LoadMore
            page={page}
            total={total}
            update={setQuery}
            spinning={spinning}
        />
        {query['fts'] ?
            <NewProducer
                name={query['fts'].split('\n')[0]}
                onCreate={() => setQuery(prev => ({...prev, 'fts': ''}))}
            />
            : null
        }
    </div>
}



export function NewProducer({name, onCreate}) {
    const save = useSave()
    const {setHashParam} = useHash()

    function handleClick() {
        const producer = {id: genId(), name}
        onCreate(producer)
        save('producer', producer)
            .then(() => {
                setHashParam('edit', 'producer/' + producer.id)
            })
    }

    return <div className="new-item-card" onClick={handleClick} role="button" tabIndex={0}>
        <span className="new-item-card__label">create</span>
        <span className="flex1">
            {name}
        </span>
        <AddIcon width="32px" height="32px" color="--primary"/>
    </div>
}




function usePanTo(map, coordinates, mapRestriction) {
    const mapRestrictionRef = useUpdatingRef(mapRestriction)

    React.useEffect(() => {
        if (!map) {return}
        if (!coordinates) {return}
        if (coordinates.coordinate_latitudine === null) {return}
        if (coordinates.coordinate_longitudine === null) {return}
        const lat = coordinates.coordinate_latitudine
        const lng = coordinates.coordinate_longitudine
        const position = {lat, lng,}
        const boudns = {
            east: lng,
            west: lng,
            north: lat,
            south: lat,
        }
        map.panToBounds(boudns, getPadding(mapRestrictionRef.current))
    }, [map, coordinates])

    function getPadding(mapRestriction) {
        const height = window.innerHeight
        const width = window.innerWidth
        const padding = {}
        const w1 = 1040
        if (width > w1) {
            padding.top = height / 2
            padding.bottom = height / 2
            if (mapRestriction === 'null-closed-closed') {
                padding.left = width / 2
                padding.right = width / 2
            }
            else {
                padding.left = w1 + (width - w1) / 2
                padding.right = (width - w1) / 2
            }
        }
        else {
            padding.left = width / 2
            padding.right = width / 2
            if (mapRestriction === 'closed-closed-closed') {
                padding.top = height / 2
                padding.bottom = height / 2
            }
            else {
                padding.top = height / 5
                padding.bottom = height / 5 * 4
            }
        }
        return padding
    }
}



function MapView({
    panToCoordinates,
    userCoordinates,
    referenceCoordinates,
    zoom,
    filter,
    onSelectProducer,
    selectedProducerId,
    onClick,
    onDoubleClick,
    children,
    mapRestriction,
}) {
    const ref = React.useRef()
    const [map, setMap] = React.useState()
    // pending reference-coordinate set, cancellable by a marker click (see below)
    const pendingMapClickRef = React.useRef(null)


    usePanTo(map, panToCoordinates, mapRestriction)

    const pins = usePins(map, filter)
    usePinClusterer(map, pins, selectedProducerId, onSelectProducer, pendingMapClickRef)

    React.useEffect(() => {
        if (!ref.current || map) {return}
        setMap(new window.google.maps.Map(ref.current, {
            center: referenceCoordinates ? {
                lat: referenceCoordinates.coordinate_latitudine,
                lng: referenceCoordinates.coordinate_longitudine,
            } : {
                lat: homeCoordinates.coordinate_latitudine,
                lng: homeCoordinates.coordinate_longitudine,
            },
            zoom: zoom || 10,
            gestureHandling: "greedy",
            disableDefaultUI: true,
            mapId: MAP_ID,
        }))
    }, [map])

    React.useEffect(() => {
        if (!map) {return}

        let timeouts = []
        let start = null
        let zoom = null
        let clear = false

        function handleMouseDown(a) {
            start = new Date()
            zoom = map.getZoom()
            clear = false
            const timeout = setTimeout(() => {
                onDoubleClick({
                    coordinate_latitudine: a.latLng.lat(),
                    coordinate_longitudine: a.latLng.lng(),
                })
            }, 1000)
            timeouts.push(timeout)
        }

        function handleMouseUp(a) {
            timeouts.forEach(t => clearTimeout(t))
            timeouts = []
            if (new Date() - start < 400 && zoom === map.getZoom() && !clear) {
                // Defer briefly: a marker click fires right after this mouseup and
                // cancels the pending set, so clicking a marker doesn't move the
                // sorting location (only clicking the empty map does).
                clearTimeout(pendingMapClickRef.current)
                pendingMapClickRef.current = setTimeout(() => {
                    pendingMapClickRef.current = null
                    onClick({
                        coordinate_latitudine: a.latLng.lat(),
                        coordinate_longitudine: a.latLng.lng(),
                    })
                }, 60)
            }
        }

        function handleDragStart() {
            clear = true
            timeouts.forEach(t => clearTimeout(t))
            timeouts = []
        }

        const listener0 = map.addListener('mousedown', handleMouseDown)
        const listener1 = map.addListener('mouseup', handleMouseUp)
        const listener2 = map.addListener('dragstart', handleDragStart)
        const listener3 = map.addListener('resize', handleDragStart)
        const listener4 = map.addListener('tilt_changed', handleDragStart)
        const listener5 = map.addListener('bounds_changed', handleDragStart)
        return () => {
            clearTimeout(pendingMapClickRef.current)
            window.google.maps.event.removeListener(listener0)
            window.google.maps.event.removeListener(listener1)
            window.google.maps.event.removeListener(listener2)
            window.google.maps.event.removeListener(listener3)
            window.google.maps.event.removeListener(listener4)
            window.google.maps.event.removeListener(listener5)
        }
    }, [map])

    return <div ref={ref} id="map" className="map-container">
        {userCoordinates ?
            <ReferenceMapMarker map={map} coordinates={userCoordinates} active={!referenceCoordinates} onClick={onClick} />
            : null
        }
        {referenceCoordinates ?
            <ReferenceMapMarker map={map} coordinates={referenceCoordinates} active />
            : null
        }
    </div>
}

function ReferenceMapMarker({map, coordinates, active, onClick}) {
    React.useEffect(() => {
        if (!coordinates) {return}
        if (!map) {return}
        const position = {lat: coordinates.coordinate_latitudine, lng: coordinates.coordinate_longitudine}
        const marker = new window.google.maps.Marker({
            position,
            icon: getIcon(active),
            map,
        })
        if (onClick) {
            marker.addListener('click', () => {
                onClick(coordinates)
            })
        }

        return () => marker.setMap(null)
    }, [map, active, coordinates])

    function getIcon(active) {
        return {
            path: 'M35.2745401,76.9537466 C23.5243881,71.8507861 14.9408366,60.8270569 13.2891064,47.6477797 C13.0996379,46.4659126 13,45.2514641 13,44 C13,43.8330728 13.0011054,43.6664039 13.0033093,43.5 C13.0011054,43.3335961 13,43.1669272 13,43 C13,22.5654643 29.5654643,6 50,6 C70.4345357,6 87,22.5654643 87,43 C87,43.1669272 86.9988946,43.3335961 86.9966907,43.5 C86.9988946,43.6664039 87,43.8330728 87,44 C87,45.2514641 86.9003621,46.4659126 86.7108936,47.6477797 C85.0591634,60.8270569 76.4756119,71.8507861 64.7254599,76.9537466 C59.0674536,82.3778153 53.582827,88.1327525 50,95 C46.417173,88.1327525 40.9325464,82.3778153 35.2745401,76.9537466 L35.2745401,76.9537466 Z',
            fillColor: active ? "#cc48de" : "gray",
            fillOpacity: active ? 0.7 : 0.6,
            strokeColor: 'white',
            strokeWeight: 2,
            scale: 0.5,
            anchor: new window.google.maps.Point(50, 100)
        }
    }

    return null
}


function useReferenceCoordinates(initial) {
    const [value, setValue] = React.useState(() => {
        const hash = window.location.hash
        if (!hash) {return}
        if (hash.indexOf('rc=') !== -1) {
            const raw_coordinates = hash.split('rc=')[1].split('&')[0]
            const [coordinate_latitudine, coordinate_longitudine] = raw_coordinates.split(',').map(c => parseFloat(c))
            const init = {coordinate_latitudine, coordinate_longitudine}
            return init
        }
    })
    return [value, setValue]
}

function GoogleMap(props) {
    return <GoogleMapWrapper>
        <MapView {...props} />
    </GoogleMapWrapper>
}



function getProductId(hash) {
    const d = hash.split('/')[2]
    if (d) {
        return d.split('&')[0]
    }
    return null
}


function ProducerQuerySwitcher() {
    const init = useInit()
    const savedQueries = React.useMemo(() => {
        return init.saved_query.filter(s => {
            return s.model === 'producer'
        })
    }, [init.saved_query])

    const {hash, setHashParam, params} = useHash()


    return <>
        {savedQueries.map(s => {
            return <Button
                key={s.id}
                title={s.name}
                active={s.id === params.query}
                onClick={() => {
                    if (s.id === params.query) {
                        setHashParam('query', null)
                    }
                    else {
                        setHashParam('query', s.id)}
                    }
                }
                active={params.query === s.id}
            >
                {getShortName(s.name)}
            </Button>
        })}
    </>

    function getShortName(name) {
        const [a, b] = name.split(' | ')
        return a
    }
}
